import { useLingui } from '@lingui/react/macro';

import type { IntroVideoDestroyTarget } from './utils/introVideoSlots';
import { MAX_FILE_SIZE_MB, MAX_VIDEO_DURATION_SECONDS } from './IntroVideoSitterPage.constants';

export function useIntroVideoUploadContent(): {
  fileTooLarge: string;
  unsupportedFileType: string;
  uploadFailed: string;
} {
  const { t } = useLingui();

  return {
    fileTooLarge: t`That video is over ${MAX_FILE_SIZE_MB} MB. Try a shorter clip or a smaller file.`,
    unsupportedFileType: t`That file type isn't supported. Upload an MP4 or MOV video.`,
    uploadFailed: t`We couldn't upload your video. Please try again.`,
  };
}

/**
 * How the destroy control reads, by what it acts on. Keyed rather than spread across
 * the callers so a new target is one edit, not one per platform plus the hook.
 */
export function useIntroVideoDestroyContent(): Record<
  IntroVideoDestroyTarget,
  { cta: string; pendingCta: string; failed: string }
> {
  const { t } = useLingui();

  return {
    video: {
      cta: t`Delete`,
      pendingCta: t`Deleting…`,
      failed: t`We couldn't delete your video. Please try again.`,
    },
    processingUpload: {
      cta: t`Cancel`,
      pendingCta: t`Cancelling…`,
      failed: t`We couldn't cancel your upload. Please try again.`,
    },
    failedUpload: {
      // Named, because this control sits on a card showing the video that stays.
      cta: t`Discard failed upload`,
      pendingCta: t`Discarding…`,
      failed: t`We couldn't discard that upload. Please try again.`,
    },
  };
}

export function useIntroVideoTranscodeTimeoutContent(): {
  title: string;
  body: string;
} {
  const { t } = useLingui();

  return {
    title: t`Taking longer than expected`,
    body: t`We're still processing this video. Check back in a few minutes.`,
  };
}

export function useIntroVideoUploadProgressContent(): {
  uploadingTitle: string;
  uploadingDescription: string;
  progressBarLabel: string;
} {
  const { t } = useLingui();

  return {
    uploadingTitle: t`Uploading your video...`,
    uploadingDescription: t`This usually takes a few seconds.`,
    progressBarLabel: t`Intro video upload progress`,
  };
}

export function useUploadedIntroVideoContent({
  uploadedOn,
  duration,
}: {
  uploadedOn: string;
  duration: string;
}): {
  uploadedLabel: string;
  playLabel: string;
  processingLabel: string;
  changeThumbnailCta: string;
  replaceCta: string;
} {
  const { t } = useLingui();

  return {
    uploadedLabel: t`Uploaded ${uploadedOn} · ${duration}`,
    playLabel: t`Play your intro video`,
    processingLabel: t`We're still processing this video. Check back in a few minutes.`,
    changeThumbnailCta: t`Change thumbnail`,
    replaceCta: t`Replace video`,
  };
}

export function useExampleIntroVideoContent({
  formattedDuration,
}: {
  /** `m:ss`, once the player has read the clip's metadata */
  formattedDuration?: string;
}): {
  dialogTitle: string;
  dismissLabel: string;
  playerDescription: string;
  duration: string | undefined;
  caption: string;
} {
  const { t } = useLingui();

  return {
    dialogTitle: t`Example intro video`,
    dismissLabel: t`Dismiss`,
    playerDescription: t`An example intro video from a Rover sitter`,
    duration: formattedDuration,
    caption: t`A great intro video from a real Rover sitter`,
  };
}

export function useIntroVideoSitterPageContent(): {
  uploadTitle: string;
  uploadDescription: string;
  fileRequirements: string;
  uploadCta: string;
  redoNote: string;
  examplesLabel: string;
  exampleVideoLabel: (position: number) => string;
  recordingTipsCta: string;
  loadFailedTitle: string;
  loadFailed: string;
} {
  const { t } = useLingui();

  return {
    loadFailedTitle: t`We couldn't load your intro video`,
    loadFailed: t`Something went wrong on our end. Please try again in a few minutes.`,
    uploadTitle: t`Record a ${MAX_VIDEO_DURATION_SECONDS}-second hello`,
    uploadDescription: t`Share your name, why you love pets, and what pet parents can expect when they book.`,
    fileRequirements: t`MP4 or MOV · Up to ${MAX_FILE_SIZE_MB} MB`,
    uploadCta: t`Upload a video`,
    redoNote: t`You can redo it anytime.`,
    examplesLabel: t`Need ideas? Watch a few example intros:`,
    exampleVideoLabel: (position: number) => t`Play example intro video ${position}`,
    recordingTipsCta: t`See recording tips`,
  };
}

export function useIntroVideoProfilePromptContent(): {
  heading: string;
  body: string;
  cta: string;
} {
  const { t } = useLingui();

  return {
    heading: t`Say hello with an intro video`,
    body: t`Add a video to your profile to help pet parents get to know you before they book.`,
    cta: t`Add intro video`,
  };
}
